import classnames from 'classnames'
import { FC, SyntheticEvent } from 'react'

import { IconButton } from '@src/quince-components/src'
import { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import {
  getIndividualTagStatus,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import RoundAddButtonMobile from '@src/quince-site-common/src/organisms/appLayout/molecules/cartPopUp/icons/roundAddButton.inline.svg'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import siteStateManager, { SearchDetails } from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { handleTrackAddedToCartAlgolia } from '@src/quince-site-common/src/services/algoliaService/algolia'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import RoundAddButtonDesktop from '../../icons/roundAddButton.inline.svg'
import { getSelectedOptionsForProductCard } from '../../product-card.helpers'
import { CardVariant, IProductCard } from '../../product-card.types'

import styles from './buy-it-now.module.scss'

export const ProductCardBuyItNow: FC<{
  productCard: IProductCard
  selectedCardVariant?: CardVariant
  addToCartClassName?: string
  siteSearchQuery?: string
  productPosition?: number
  productSearchObjectId?: string
  productClickTrackConfig?: TrackConfig
}> = ({
  productCard,
  selectedCardVariant,
  addToCartClassName,
  siteSearchQuery,
  productPosition,
  productSearchObjectId,
  productClickTrackConfig,
}) => {
  const searchAlgoliaQueryId =
    useAppSelector<SearchDetails>(siteStateManager, siteStateManager.getSearchDetails).searchAlgoliaQueryId || ''

  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const RoundAddButton = isMobile ? RoundAddButtonMobile : RoundAddButtonDesktop

  const handleAddToCartClick = (event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const product = productCard.product
    const buyNowClickTrackConfig: TrackConfig = {
      trackGA: true,
      trackInternal: true,
      category: 'checkout',
      action: 'click_quick_shop',
      context: {
        ...productClickTrackConfig?.context,
        product_id: product.relatedIds.shopifyId,
        internalProductId: `${product.productId}_${selectedCardVariant?.displayConfig.value}`,
        product_search_query: siteSearchQuery,
        product_position: productPosition,
        product_price: selectedCardVariant?.price.salePrice,
        name: product.title,
        color_name: selectedCardVariant?.displayConfig.value,
        color_variant: product.colorVariants,
        rating: product.reviews.averageRating,
        custom_variants: product.customVariants,
        oos_flag: getIndividualTagStatus((selectedCardVariant?.tags ?? []).map(item => item.value as ProductTagKey))
          .sold_out,
      },
    }

    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, buyNowClickTrackConfig)

    handleTrackAddedToCartAlgolia({
      objectID: productSearchObjectId ?? '',
      queryID: searchAlgoliaQueryId,
    })

    // zero price products don't trigger buy now popup
    if (!selectedCardVariant?.price.salePrice) return

    productBuyNowStateManager.showBuyNowPopUp({
      productCard: productCard,
      selectedOptions: getSelectedOptionsForProductCard(selectedCardVariant),
      searchQuery: siteSearchQuery,
      position: productPosition,
      productClickTrackConfig: productClickTrackConfig,
    })
  }

  return (
    <IconButton
      className={classnames(styles.addToCart, addToCartClassName)}
      iconClassName={styles.icon}
      onClick={handleAddToCartClick}
      iconSvg={RoundAddButton}
      ariaLabel="Buy it now"
      dataTestId="buy-now"
    />
  )
}
